import {
  ActivityDetailsFragment,
  ActivityPaymentFragment,
  ActivityPaymentFields,
  ActivityPaymentChanges,
  WorkUnit,
  WorkOrderCategory,
  ActivityMetricScope,
} from "../../../lib/graphql";
import { Typography, Table, Switch, Space, Checkbox, Row, Col } from "antd";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import {
  formatWageWithMetric,
  tableAddEditDiscardColumn,
} from "../../../lib/formats";
import {
  usePositionOptions,
  useLocalityOptions,
  useActivityPaymentUpdate,
  useCurrentUser,
  useActivityPaymentDiscard,
  useActivityPaymentCreate,
  useWorkUnitFormatters,
} from "../../../lib/hooks";
import {
  ItemSidebar,
  DiscardItem,
  NewForm,
  EditForm,
  useItemSidebarContext,
} from "../../shared";
import { useState } from "react";
import { Form, InputNumber, Rules, SelectField } from "../../form";
import { ItemFieldFormConfig } from "../../shared/ListView/ItemForm";
import { CropSelect } from "../CropSelect";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { filterColumns } from "../../../lib/utils";
import { shouldUpdate } from "../../form/Form";

function PaymentTable({
  activity,
  wageUnit,
  hideCrops,
  readonly,
}: {
  activity: ActivityDetailsFragment;
  wageUnit: WorkUnit;
  hideCrops?: boolean;
  readonly?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const { setCurrentAction } = useItemSidebarContext();
  const { currentTenant } = useCurrentUser();

  const payments = activity.payments.filter(
    (p) => p.wageUnit === wageUnit && (showAll || !p.discardedAt)
  );

  return (
    <div style={{ position: "relative", textAlign: "right" }}>
      <Space style={{ marginBottom: "4px" }}>
        <FormattedMessage id="showDiscarded" />
        <Switch
          unCheckedChildren={<EyeInvisibleOutlined />}
          checkedChildren={<EyeOutlined />}
          onChange={(checked) => setShowAll(checked)}
        />
      </Space>

      <Table
        dataSource={payments}
        pagination={false}
        rowKey="id"
        rowClassName={(v) => (v.discardedAt ? "row-disabled" : "")}
        columns={filterColumns([
          {
            title: <FormattedMessage id="wage" defaultMessage="wage" />,
            dataIndex: "wage",
            render: (_, p) =>
              formatWageWithMetric(
                p,
                currentTenant.currencyCode,
                activity.progressUnit
              ),
          },
          currentTenant.features.employees && {
            title: <FormattedMessage id="positions" />,
            dataIndex: ["position", "name"],
            render: (name) =>
              name || (
                <FormattedMessage
                  id="positions.all"
                  defaultMessage="allPositions"
                />
              ),
          },
          {
            title: <FormattedMessage id="localities" />,
            dataIndex: ["locality", "name"],
            render: (name) =>
              name || (
                <FormattedMessage
                  id="localities.all"
                  defaultMessage="allLocalities"
                />
              ),
          },
          !hideCrops
            ? {
                title: <FormattedMessage id="crops" />,
                dataIndex: ["crop", "name"],
                render: (name) =>
                  name || (
                    <FormattedMessage
                      id="crops.all"
                      defaultMessage="allCrops"
                    />
                  ),
              }
            : {},
          ...(readonly
            ? []
            : [
                tableAddEditDiscardColumn<ActivityPaymentFragment>({
                  onNew: () => {
                    setCurrentAction("newPayment", {
                      wageUnit,
                    });
                  },
                  onEdit: (p) => {
                    setCurrentAction("editPayment", p);
                  },
                  onDiscard: (p) => {
                    setCurrentAction("discardPayment", p);
                  },
                  activity,
                  wageUnit,
                }),
              ]),
        ])}
      />
    </div>
  );
}

const messages = defineMessages({
  payment: {
    id: "activities.payment",
    defaultMessage: "payment",
  },
  allPositions: { id: "positions.all", defaultMessage: "allPositions" },
  applyDualWages: {
    id: "activities.applyDualWages",
    defaultMessage: "Combine Daily Wage and Piecework Wage",
  },
});

interface ActivityPaymentsProps {
  activity: ActivityDetailsFragment;
  readonly?: boolean;
}

export function ActivityPayments({
  activity,
  readonly,
}: ActivityPaymentsProps) {
  const intl = useIntl();
  const entityName = intl.formatMessage(messages.payment);
  const { currentState: currentPayment } =
    useItemSidebarContext<ActivityPaymentFragment>();
  const { currentTenant } = useCurrentUser();
  const { formatWorkUnit } = useWorkUnitFormatters();

  const metricOptions = activity.activityMetrics
    .filter((m) => m.scope === ActivityMetricScope.Employee)
    .map(({ metric }) => ({
      key: metric.id,
      group: metric.name,
      label: <FormattedMessage id="per" values={{ value: metric.unit.abbr }} />,
      metric,
    }));

  const perProgress = intl.formatMessage(
    { id: "per" },
    { value: activity.progressUnit.abbr }
  );

  const hideCrops =
    activity.workOrderCategory !== WorkOrderCategory.Agricultural &&
    !activity.payments.find((p) => p.crop); // show crop if activity was agricultural

  const fields: (
    payment: ActivityPaymentFragment
  ) => ItemFieldFormConfig<ActivityPaymentFields | ActivityPaymentChanges>[] = (
    payment: ActivityPaymentFragment
  ) => [
    {
      label: entityName,
      name: "wage",
      type: "custom",
      rules: [Rules.gtEqZero],
      render: () => (
        <InputNumber
          addonBefore={currentTenant.currency.symbol}
          precision={4}
          addonAfter={
            payment.wageUnit === WorkUnit.Piecework ? (
              metricOptions.length ? (
                <Form.Item name="metricId" compact>
                  <SelectField
                    style={{ width: "150px" }}
                    allowClear={false}
                    showSearch={false}
                    showGroups
                    disabled={readonly}
                    options={[
                      {
                        key: "",
                        group: intl.formatMessage({ id: "progress" }),
                        label: perProgress,
                      },
                      ...metricOptions,
                    ]}
                  />
                </Form.Item>
              ) : (
                perProgress
              )
            ) : (
              formatWorkUnit(payment.wageUnit)
            )
          }
        />
      ),
    },
    {
      label: <FormattedMessage id="positions.entityName" />,
      name: "positionId",
      type: "select",
      showGroups: true,
      placeholder: intl.formatMessage(messages.allPositions),
      optionsHook: usePositionOptions,
      hidden: !currentTenant.features.employees,
    },
    {
      label: <FormattedMessage id="localities.entityName" />,
      name: "localityId",
      type: "select",
      placeholder: (
        <FormattedMessage id="localities.all" defaultMessage="allLocalities" />
      ),
      optionsHook: useLocalityOptions,
    },
    {
      label: <FormattedMessage id="crops.entityName" />,
      name: "cropId",
      type: "custom",
      hidden: hideCrops,
      render: () => (
        <CropSelect
          placeholder={
            <FormattedMessage id="crops.all" defaultMessage="allCrops" />
          }
        />
      ),
    },
    {
      type: "custom",
      render: ({ bag, setBag, form }) =>
        payment.wageUnit === WorkUnit.Piecework && (
          <Checkbox
            key="useRange"
            defaultChecked={bag.useRange}
            onChange={(e) => {
              setBag({ useRange: e.target.checked });
              if (!e.target.checked)
                form.setFields([
                  { name: "minValue", value: null },
                  { name: "maxValue", value: null },
                ]);
            }}
          >
            <FormattedMessage
              id="activities.payments.addRange"
              defaultMessage="Add Range"
            />
          </Checkbox>
        ),
    },
    {
      type: "custom",
      render: ({ bag }) =>
        bag.useRange && (
          <Form.Item noStyle shouldUpdate={shouldUpdate("metricId")}>
            {({ getFieldValue }) => {
              const metric = metricOptions.find(
                (o) => o.key === getFieldValue("metricId")
              )?.metric;

              const abbr = metric?.unit?.abbr || activity.progressUnit.abbr;

              return (
                <Row gutter={8} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Form.Item
                      name="minValue"
                      label={
                        <FormattedMessage
                          id="activities.payments.minValue"
                          defaultMessage="Min Value"
                        />
                      }
                      rules={[Rules.gtEqZero]}
                      compact
                    >
                      <InputNumber addonAfter={abbr} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="maxValue"
                      label={
                        <FormattedMessage
                          id="activities.payments.maxValue"
                          defaultMessage="Max Value"
                        />
                      }
                      rules={[Rules.gtEqZero]}
                      compact
                    >
                      <InputNumber addonAfter={abbr} />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        ),
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        <FormattedMessage
          id="activities.payment.sectionHint"
          defaultMessage="sectionHint"
        />
      </Typography.Paragraph>

      <ItemSidebar
        item={currentPayment}
        sidebarActions={{
          editPayment: ({ item, closeSidebar }) =>
            item && (
              <EditForm
                item={item}
                entityName={entityName}
                mutation={useActivityPaymentUpdate}
                mutationOptions={{ refetchQueries: ["Activity"] }}
                onMutate={(result) => result.activityPaymentUpdate}
                onSuccess={closeSidebar}
                onCancel={closeSidebar}
                fields={fields(item)}
                initBag={(p) => ({ useRange: p?.minValue || p?.maxValue })}
                formValues={(p) => ({
                  wage: p.wage,
                  wageUnit: p.wageUnit,
                  localityId: p.locality?.id,
                  positionId: p.position?.id,
                  metricId: p.metric?.id || "",
                  minValue: p.minValue,
                  maxValue: p.maxValue,
                  cropId: p.crop?.id,
                })}
              />
            ),
          newPayment: ({ item, closeSidebar }) =>
            item && (
              <NewForm
                entityName={entityName}
                mutation={useActivityPaymentCreate}
                mutationOptions={{ refetchQueries: ["Activity"] }}
                onMutate={(result) => result.activityPaymentCreate}
                onClose={closeSidebar}
                fields={fields(item)}
                formValues={() =>
                  ({
                    wageUnit: item.wageUnit,
                    metricId: item.metric?.id || "",
                    activityId: activity.id,
                  } as ActivityPaymentFields)
                }
              />
            ),
          discardPayment: ({ item, closeSidebar }) =>
            item && (
              <DiscardItem
                item={item}
                entityName={entityName}
                mutation={useActivityPaymentDiscard}
                onMutate={(result) => result.activityPaymentDiscard}
                mutationOptions={{ refetchQueries: ["ActivityPayments"] }}
                onCancel={closeSidebar}
                onSuccess={closeSidebar}
              />
            ),
        }}
      />

      <br />
      <Typography.Paragraph>
        <FormattedMessage
          id="activities.payment.dailyTitle"
          defaultMessage="dailyTitle"
        />
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">
        <FormattedMessage
          id="activities.payment.dailyHint"
          defaultMessage="dailyHint"
        />
      </Typography.Paragraph>

      <PaymentTable
        activity={activity}
        wageUnit={WorkUnit.Day}
        hideCrops={hideCrops}
        readonly={readonly}
      />

      {currentTenant.features.pieceworkWages && (
        <>
          <br />
          <br />
          <Typography.Paragraph>
            <FormattedMessage
              id="activities.payment.pieceworkTitle"
              defaultMessage="pieceworkTitle"
            />
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            <FormattedMessage
              id="activities.payment.pieceworkHint"
              defaultMessage="pieceworkHint"
            />
          </Typography.Paragraph>

          <PaymentTable
            activity={activity}
            wageUnit={WorkUnit.Piecework}
            hideCrops={hideCrops}
            readonly={readonly}
          />

          <br />
          {activity.applyDualWages && readonly ? (
            <Space>
              <Switch checked disabled />
              {intl.formatMessage(messages.applyDualWages)}
            </Space>
          ) : (
            !readonly && (
              <Form.Item
                name="applyDualWages"
                valuePropName="checked"
                label={intl.formatMessage(messages.applyDualWages)}
                extra={
                  <Form.Item
                    noStyle
                    shouldUpdate={shouldUpdate("applyDualWages")}
                  >
                    {({ getFieldValue }) => {
                      return getFieldValue("applyDualWages") ? (
                        <FormattedMessage
                          id="activities.payment.dualWagesExample"
                          defaultMessage="✓ Enabled: Worker earns both payments (e.g., {dailyWage} for the day + {examplePiecework} for completing work = {totalExample})."
                          values={{
                            dailyWage: `${currentTenant.currency.symbol}100`,
                            examplePiecework: `${currentTenant.currency.symbol}500 (10 units)`,
                            totalExample: `${currentTenant.currency.symbol}600`,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="activities.payment.dualWagesDisabledExample"
                          defaultMessage="○ Disabled: Worker is paid by daily wage ({dailyWage}) or piecework wage ({pieceworkWage}) for piecework activities."
                          values={{
                            dailyWage: `${currentTenant.currency.symbol}100`,
                            pieceworkWage: `${currentTenant.currency.symbol}50/unit`,
                          }}
                        />
                      );
                    }}
                  </Form.Item>
                }
              >
                <Switch disabled={readonly} />
              </Form.Item>
            )
          )}
        </>
      )}
    </>
  );
}
